import { IHTVPlatform } from './platform'
import { LocalChannel } from '../interfaces/local-channel'
import { Injectable } from '@angular/core'
import { ChannelPosition } from '../pages/channel/channel-position'
import { MonitoringMemoryData, MonitoringStorageData, SystemData } from '../interfaces/monitoring'
import { LocalAppLaunchMode, TVApp } from '../interfaces/local-app'
import { ChannelTrackData, ChannelTrackDataFlags, CurrentChannelData } from '../interfaces/channel-track-data'
import { PowerState } from '../interfaces/power-state'
import { BehaviorSubject, Subscription } from 'rxjs'
import { NetworkData, NetworkState } from '../interfaces/network'
import { WebOSInternal } from './webos-internal'
import { LocalMediaFile, LogService, Media } from 'htv-lib'
import { DVBTChannel, UDPChannel, WebChannel } from '../interfaces/channel'
import { environment } from '../environments/environment'
import { StatusSuccessDto } from '../interfaces/dto/status-response-dto'
import { RouterService } from '../services/router.service'
import { GoogleCastState } from '../interfaces/local-googlecast'
import { ExternalInputData } from '../services/external-input.service'
import { VODVideo } from '../interfaces/vod'

@Injectable({
  providedIn: 'root',
})
export class WebOSPlatform implements IHTVPlatform {
  private webosInternal: WebOSInternal
  private webOSVersion!: string

  networkState?: NetworkState
  networkStateTimer: any
  readonly networkStateTimerDelayInMs = 1000

  powerState?: PowerState
  powerStateTimer: any
  readonly powerStateTimerDelayInMs = 1000

  allowSubtitle = false
  isSubtitleEnabled = false
  subtitleOffIndex?: number
  private mediaSubtitleType = 'internal_subtitle'
  // webOS media player bug: a few seconds after a subtitle track is displayed (after the seek_done event),
  // idcap://tv/media/audiolanguage/get returns the subtitle index instead of the audio index,
  // although the audio actually playing does not change.
  // The audio index is therefore read once per channel and then only updated by setWebAudioTrack.
  private mediaAudioIndex?: number

  gcInitializedSubject?: BehaviorSubject<boolean | undefined>
  gcStateSubject?: BehaviorSubject<GoogleCastState | undefined>
  gcStateSubscription?: Subscription
  gcStatus = ''

  private readonly GOOGLECAST_TOKEN =
    'm/519+Vb5eQuopwVjegauGMdwuhHSSMVjf8FrHre7zptWR+pEBZiyHNusojmvqn4pnYF3EgXeFo2RaiMp2Ws4vxQl5te00/79h7OVZoGyjdenFdfjYNwc7iFpD4Acljj2itXCSMJM3BM+DJWXN5hiYTWXCO8hSvhmglM1EUiXpVyFC1xdOCE7gP1NrgDfw8z7uO5kckRaF2B8OcODmosTux5Wonqj96pyTBd7W84lV7AER1zoe2YHdAXJV1vd3W1laAafAv+L37hMrDsMQpD4EeqHVo1akNWuUGO8CywptLDj9ca9R37R+3u0pElzotdIK6ljG/0OWHobR9DdW6ejw=='
  private readonly NETFLIX_TOKEN =
    'DGgKIkbpuPZ2XHlsXBeXRdXTaxAyNwtIs6F4+Jj02jlJ2aihrjvswnZ63rWrc0ElMKH0t6CKC+wEce8/gYMi5wbdY8F1o+HUY7EKuRDAOvbtKl1678qXiA9BnPWxCvz0KAajjpRvvU5SC6RKsGw0lBRjfBF+U+XAc/0oIPP1xPPzYqm7BKJAGy8UBr9fXY+HbA+9hNTVsFc08LfW7pGFeFVYpl9ftCQR1sbG1qB+We0VyNWvlyEi9ih84GWY4V5jajGrX5URwakUv4QVGnCk48CSODL3/WzywPsIgtYleBh0LhUCKKiG8h0EeznKXjK5aEMRxnS+Yj4k2LnY5POSFg=='

  constructor(
    private logService: LogService,
    private routerService: RouterService
  ) {
    this.logService.debug('WebOSPlatform loaded')
    this.webosInternal = new WebOSInternal(logService)
  }

  id(): string {
    return 'WebOSPlatform'
  }

  async init(): Promise<void> {
    await this.loadScript('assets/lib/idcap-1.1.1.js')
    await this.loadScript('assets/lib/hcap-1.24.12.5977.js')

    this.webOSVersion = await this.webosInternal.getStringProperty('webos_version')

    // Enable debug mode in dev environment to have it enabled if there is a status issue
    if (environment.enableDebug) {
      this.webosInternal.checkAndUpdateDebugMode(environment.enableDebug)
    }

    // I believe this gives full control to the App
    // https://procentric.developer.lge.com/api-references/hcap-app-api/mode
    this.webosInternal.setAppVisibilityAndKeyControlMode()

    // Configure hotel mode
    this.webosInternal.setHotelMode()

    // TODO: Should use status.deviceResolution, move call to afterStatusHook()
    // TODO: Should reboot if changed
    // Default to 1920x1080
    this.webosInternal.setDisplayResolution('1920x1080')

    // Disable floating UI
    this.webosInternal.setTVChannelFloatingUI('0')

    // When turning ON the TV, show Pro:Centric App and blocks audio/video
    // https://procentric.developer.lge.com/docs/hcap-idcap-app-development/frequently-used-properties#bootsequenc_option
    this.webosInternal.setBootSequenceOption('1')

    // Channels can be changed only by the HCAP app
    this.webosInternal.setApplicationChannelControl('1')

    // Disable TV volume UI
    this.webosInternal.setTvVolumeUI('0')

    // Disable default subtitle language
    this.webosInternal.setDefaultSubtitleLanguage('off')

    // TODO: Should use data from IPTV configuration
    // Set default audio language
    this.webosInternal.setDefaultAudioLanguage('fr')

    this.webosInternal.registerRemoteKeys()
    this.webosInternal.initStorage()
  }

  loadScript(path: string): Promise<boolean> {
    return new Promise(resolve => {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.onload = () => {
        this.logService.debug('Loaded script ' + path)
        resolve(true)
      }
      script.onerror = (err: any) => {
        this.logService.error('Could not load script ' + path)
        this.logService.error(err)
        resolve(false)
      }
      script.src = path
      document.head.appendChild(script)
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  afterRegistrationHook(deviceId: string | undefined): void {
    return
  }

  async afterStatusHook(status: StatusSuccessDto): Promise<void> {
    let requireReboot = false

    let enableDebug = environment.enableDebug
    if (status.htvConfig) {
      const htvConfig = status.htvConfig

      if (htvConfig.enableDebug) {
        enableDebug = true
      }

      if (htvConfig && htvConfig.netflix) {
        if (htvConfig.netflix.enabled) {
          this.initNetflixApp()
        }
      }

      this.webosInternal.configureInstantPowerOnMode(status.htvConfig)
    }

    await this.webosInternal.checkAndUpdateRoomNumber(status.deviceName)

    const tvName = `[TV] ${status.deviceName}`
    const tvNameUpdated = await this.webosInternal.checkAndUpdateTVName(tvName)
    if (tvNameUpdated) {
      requireReboot = true
    }

    const debugModeUpdated = await this.webosInternal.checkAndUpdateDebugMode(enableDebug)
    if (debugModeUpdated) {
      requireReboot = true
    }

    if (requireReboot) {
      this.logService.info('Configuration has changed, reboot is required')
      this.reboot()
    }

    return Promise.resolve()
  }

  enableGuestMode(): Promise<void> {
    return this.setSecurityLevel('2')
  }

  disableGuestMode(): Promise<void> {
    return this.setSecurityLevel('1')
  }

  async clearData(): Promise<void> {
    await this.setSecurityLevel('1')
    this.reboot()
    return Promise.resolve()
  }

  // Used to clear credentials and applications data of user
  // 1 : security level 1. delete personal information in TV when turning TV off.
  // 2 : security level 2. maintain personal information in TV when turning TV off.
  // Must call a reboot afterwards
  async setSecurityLevel(level: string): Promise<void> {
    const currentLevel = await this.webosInternal.getSecurityLevel()
    if (currentLevel != level) {
      this.webosInternal.setSecurityLevel(level)
    }
  }

  async getSystemData(): Promise<SystemData> {
    return {
      model: await this.webosInternal.getModel(),
      manufacturer: await this.webosInternal.getManufacturer(),
      serialNumber: await this.webosInternal.getSerialNumber(),
      firmwareVersion: await this.webosInternal.getFirmwareVersion(),
      appVersion: environment.appVersion,
    }
  }

  async getPlatformData(): Promise<object> {
    const data: { [key: string]: any } = {}

    data['userAgent'] = navigator.userAgent

    data['idpn'] = await this.webosInternal.getStringProperty('idpn')
    data['webos_version'] = await this.webosInternal.getStringProperty('webos_version')
    data['model_name'] = await this.webosInternal.getStringProperty('model_name')
    data['serial_number'] = await this.webosInternal.getStringProperty('serial_number')
    data['platform_version'] = await this.webosInternal.getStringProperty('platform_version')
    data['firmware_version'] = await this.webosInternal.getStringProperty('firmware_version')
    data['xait_version'] = await this.webosInternal.getStringProperty('xait_version')
    data['lgudid'] = await this.webosInternal.getStringProperty('lgudid')
    data['micom_version'] = await this.webosInternal.getStringProperty('micom_version')
    data['boot_version'] = await this.webosInternal.getStringProperty('boot_version')
    data['platform_src_revision'] = await this.webosInternal.getStringProperty('platform_src_revision')
    data['language'] = await this.webosInternal.getStringProperty('language')
    data['country'] = await this.webosInternal.getStringProperty('country')
    data['platform_manufacturer'] = await this.webosInternal.getStringProperty('platform_manufacturer')
    data['hardware_version'] = await this.webosInternal.getStringProperty('hardware_version')

    data['hodel_mode'] = await this.webosInternal.getHotelMode()

    return data
  }

  getCpuUsage(): Promise<number> {
    return this.webosInternal.getCpuUsage()
  }

  getMemoryData(): Promise<MonitoringMemoryData> {
    return this.webosInternal.getMemoryUsage()
  }

  getStorageData(): Promise<MonitoringStorageData[]> {
    return this.webosInternal.getStorageData()
  }

  // There is no API available on Hotel TVs
  getTemperature(): Promise<number> {
    return Promise.resolve(0)
  }

  getValue(key: string): any {
    return localStorage.getItem(key)
  }

  setValue(key: string, value: any): void {
    localStorage.setItem(key, value)
  }

  removeValue(key: string): void {
    localStorage.removeItem(key)
  }

  clearValues(): void {
    localStorage.clear()
  }

  reboot(): void {
    this.webosInternal.reboot()
  }

  async setLanguage(code: string): Promise<void> {
    try {
      const currentLanguage = await this.webosInternal.getLocale()
      if (currentLanguage == code) {
        this.logService.debug(`[WebOS] (setLanguage) Language ${code} already set`)
      } else {
        const localeList = await this.webosInternal.getLocaleList()
        if (localeList.includes(code)) {
          await this.webosInternal.setLocale(code)
        } else {
          this.logService.error(`[WebOS] (setLanguage) Language code ${code} could not be found in TV languages`)
        }
      }
    } catch (err: any) {
      this.logService.error(`[WebOS] (setLanguage) error = ${err?.message}`)
    }
  }

  async getPowerState(): Promise<PowerState | undefined> {
    return this.webosInternal.getPowerState()
  }

  setPowerState(state: PowerState): void {
    this.webosInternal.setPowerState(state)
  }

  registerPowerStateListener(subject: BehaviorSubject<PowerState | undefined>): void {
    if (this.powerStateTimer) {
      clearInterval(this.powerStateTimer)
    }
    this.powerStateTimer = setInterval(async () => {
      const powerState = await this.getPowerState()
      if (this.powerState != powerState) {
        this.powerState = powerState
        subject.next(powerState)
      }
    }, this.powerStateTimerDelayInMs)
  }

  getNetworkState(): Promise<NetworkState> {
    return this.webosInternal.getNetworkState()
  }

  getNetworkData(): Promise<NetworkData> {
    return this.webosInternal.getNetworkData()
  }

  registerNetworkStateListener(subject: BehaviorSubject<NetworkState>): void {
    if (this.networkStateTimer) {
      clearInterval(this.networkStateTimer)
    }
    this.networkStateTimer = setInterval(async () => {
      const networkState = await this.getNetworkState()
      if (this.networkState != networkState) {
        this.networkState = networkState
        subject.next(networkState)
      }
    }, this.networkStateTimerDelayInMs)
  }

  registerApplicationInstalledListener(subject: BehaviorSubject<string[]>): void {
    this.webosInternal.registerApplicationInstalledListener(subject)
  }

  registerApplicationFocusListener(subject: BehaviorSubject<boolean>): void {
    this.webosInternal.registerApplicationFocusListener(subject)
  }

  async getAppList(): Promise<TVApp[]> {
    try {
      const appList = await this.webosInternal.getAppList()
      return appList.map(app => ({
        id: app.appId,
        name: app.title,
        iconPath: app.iconPath ?? '',
        version: app.version ?? '',
        internalApp: app,
      }))
    } catch (err: any) {
      this.logService.error(`[WebOS] Could not retrieve applications list`)
      return []
    }
  }

  startApp(appId: string): void {
    this.webosInternal.startApp(appId, {}, false)
  }

  /**
   * https://procentric.developer.lge.com/docs/device-setup/netflix
   */
  startNetflixApp(appId: string, hotelId: string, launchMode: LocalAppLaunchMode): Promise<void> {
    let reason: string
    switch (launchMode) {
      case LocalAppLaunchMode.APPS:
      case LocalAppLaunchMode.MENU:
        reason = 'launcher'
        break
      case LocalAppLaunchMode.BOOT:
        reason = 'boot'
        break
      case LocalAppLaunchMode.REMOTE:
        reason = 'hotKey'
        break
    }

    const params = {
      reason: reason,
      params: {
        hotel_id: hotelId,
        launcher_version: '1.0',
      },
    }
    const noSplash = false
    return this.webosInternal.startApp(appId, params, noSplash)
  }

  registerChannelListener(subject: BehaviorSubject<boolean>): void {
    this.webosInternal.registerChannelChangedListener(subject)
  }

  // https://procentric.developer.lge.com/docs/hcap-idcap-app-development/media#how-to---case-by-case
  private isMediaVideoLayer = false

  async checkMediaVideoLayer(mediaVideoLayerRequested: boolean): Promise<void> {
    if (this.isMediaVideoLayer) {
      if (mediaVideoLayerRequested) {
        // Media API into Media API
        // nothing to do
      } else {
        // Media API into Channel API
        // https://procentric.developer.lge.com/docs/hcap-idcap-app-development/media#why-follow-these-steps
        // Waiting 500ms after shutdown to play next
        await this.webosInternal.shutdownMediaAPI()
        this.isMediaVideoLayer = false
      }
    } else {
      if (mediaVideoLayerRequested) {
        // Channel API into Media API
        await this.webosInternal.startupMediaAPI()
        this.isMediaVideoLayer = true
      } else {
        // Channel API into Channel API
        // nothing to do
      }
    }
  }

  async startBroadcastChannel(channel: LocalChannel, pos: ChannelPosition): Promise<void> {
    const source = channel.source as DVBTChannel
    await this.webosInternal.setVideoSize(pos)
    await this.checkMediaVideoLayer(false)
    this.webosInternal.startBroadcastChannel(source)
  }

  async startUDPChannel(channel: LocalChannel, pos: ChannelPosition): Promise<void> {
    const source = channel.source as UDPChannel
    await this.webosInternal.setVideoSize(pos)
    await this.checkMediaVideoLayer(false)
    this.webosInternal.startUDPChannel(source)
  }

  async startWebChannel(channel: LocalChannel, pos: ChannelPosition): Promise<void> {
    const source = channel.source as WebChannel
    this.mediaAudioIndex = undefined
    await this.webosInternal.setVideoSize(pos)
    await this.checkMediaVideoLayer(true)
    this.webosInternal.startWebChannel(source)
  }

  stopBroadcastChannel(): void {
    this.webosInternal.stopChannel()
  }

  stopUDPChannel(): void {
    this.webosInternal.stopChannel()
  }

  stopWebChannel(): void {
    this.webosInternal.stopWebChannel()
  }

  getCurrentChannel(): Promise<any> {
    if (this.isMediaVideoLayer) {
      // The channel API has no current channel while the media API is playing
      return Promise.resolve({ media: true })
    }
    return this.webosInternal.getCurrentChannel()
  }

  getBroadcastCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    return this.getCurrentProgramData()
  }

  getUDPCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    return this.getCurrentProgramData()
  }

  async getWebCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    const channelData: CurrentChannelData = {
      audioTracks: [],
      subtitleTracks: [],
      isSubtitleEnabled: false,
      currentAudioTrack: undefined,
      currentSubtitleTrack: undefined,
    }

    // 'xx' is reported by the player when the language is unknown or unavailable
    const toTracks = (list: string[]): ChannelTrackData[] =>
      list
        .map(
          (lang: string, index: number): ChannelTrackData => ({ index: index, lang: lang, data: undefined, flags: [] })
        )
        .filter(track => track.lang !== 'xx')

    try {
      const audio = await this.webosInternal.getMediaAudioLanguage()
      channelData.audioTracks = toTracks(audio.list)
      if (this.mediaAudioIndex === undefined && channelData.audioTracks.some(track => track.index === audio.index)) {
        this.mediaAudioIndex = audio.index
      }
      channelData.currentAudioTrack = channelData.audioTracks.find(track => track.index === this.mediaAudioIndex)
    } catch (err) {
      this.logService.error(`[getWebCurrentProgramData] Could not retrieve audio tracks`)
      return Promise.reject()
    }

    try {
      const subtitle = await this.webosInternal.getMediaSubtitle()
      channelData.subtitleTracks = toTracks(subtitle.list)
      if (subtitle.type !== 'none') {
        this.mediaSubtitleType = subtitle.type
        channelData.currentSubtitleTrack = channelData.subtitleTracks.find(track => track.index === subtitle.index)
        channelData.isSubtitleEnabled = channelData.currentSubtitleTrack !== undefined
      }
      this.isSubtitleEnabled = channelData.isSubtitleEnabled
    } catch (err) {
      this.logService.debug(`[getWebCurrentProgramData] Could not retrieve subtitle`)
    }

    return Promise.resolve(channelData)
  }

  private async getCurrentProgramData(): Promise<CurrentChannelData | undefined> {
    const channelData: CurrentChannelData = {
      audioTracks: [],
      subtitleTracks: [],
      isSubtitleEnabled: false,
      currentAudioTrack: undefined,
      currentSubtitleTrack: undefined,
    }

    try {
      const audioLanguageList = await this.webosInternal.getAudioLanguageList()
      const audioTracks = audioLanguageList.map((lang: string, index: number) => {
        const flags: string[] = []
        const langArr = lang.split('_')
        if (langArr.length > 1) {
          if (langArr[1] && langArr[1] == 'AD') {
            flags.push(ChannelTrackDataFlags.VISUAL_IMPAIRED)
          }
        }
        return {
          index: index,
          lang: langArr[0],
          data: undefined,
          flags: flags,
        }
      })
      channelData.audioTracks = audioTracks
    } catch (err) {
      this.logService.error(`[getCurrentProgramData] Could not retrieve audio list`)
      return Promise.reject()
    }

    try {
      const currentAudioIndex = await this.webosInternal.getAudioLanguageCurrentIndex()
      if (channelData.audioTracks && channelData.audioTracks[currentAudioIndex]) {
        channelData.currentAudioTrack = channelData.audioTracks[currentAudioIndex]
      }
    } catch (err) {
      this.logService.error(`[getCurrentProgramData] Could not retrieve current audio index`)
      return Promise.reject()
    }

    try {
      const subtitleList = await this.webosInternal.getSubtitleList()
      const subtitleTracks = subtitleList.map((lang: string, index: number) => {
        const flags: string[] = []
        const langArr = lang.split('_')
        if (langArr.length > 1) {
          if (langArr[1] && langArr[1] == 'HOH') {
            flags.push(ChannelTrackDataFlags.HEARING_IMPAIRED)
          }
        }
        return {
          index: index,
          lang: langArr[0],
          data: undefined,
          flags: flags,
        }
      })
      channelData.subtitleTracks = subtitleTracks.filter(s => s.lang !== 'off')

      const offTrack = subtitleTracks.find(s => s.lang === 'off')
      this.subtitleOffIndex = offTrack ? offTrack.index : undefined
    } catch (err) {
      this.logService.error(`[getCurrentProgramData] Could not retrieve subtitle list`)
      return Promise.reject()
    }

    try {
      const currentSubtitleIndex = await this.webosInternal.getSubtitleCurrentIndex()
      if (this.subtitleOffIndex && this.subtitleOffIndex === currentSubtitleIndex) {
        this.isSubtitleEnabled = false
        channelData.isSubtitleEnabled = false
        channelData.currentSubtitleTrack = undefined
      } else {
        const subtitleTrack = channelData.subtitleTracks.find(track => track.index === currentSubtitleIndex)
        if (subtitleTrack) {
          this.isSubtitleEnabled = true
          channelData.isSubtitleEnabled = true
          channelData.currentSubtitleTrack = subtitleTrack
        } else {
          this.isSubtitleEnabled = false
          channelData.isSubtitleEnabled = false
          channelData.currentSubtitleTrack = undefined
        }
      }
    } catch (err) {
      this.logService.debug(`[getCurrentProgramData] Could not retrieve current subtitle index`)
      return Promise.reject()
    }

    return Promise.resolve(channelData)
  }

  setBroadcastAudioTrack(track: ChannelTrackData): void {
    this.webosInternal.setAudioLanguageCurrentIndex(track.index)
  }

  setUDPAudioTrack(track: ChannelTrackData): void {
    this.webosInternal.setAudioLanguageCurrentIndex(track.index)
  }

  setWebAudioTrack(track: ChannelTrackData): void {
    this.mediaAudioIndex = track.index
    this.webosInternal.setMediaAudioLanguageIndex(track.index)
  }

  setBroadcastSubtitleTrack(track: ChannelTrackData): void {
    this.setSubtitleCurrentIndex(track)
  }

  setUDPSubtitleTrack(track: ChannelTrackData): void {
    this.setSubtitleCurrentIndex(track)
  }

  // The track must be selected before the display is enabled
  async setWebSubtitleTrack(track: ChannelTrackData): Promise<void> {
    if (this.allowSubtitle) {
      try {
        await this.webosInternal.setMediaSubtitle(this.mediaSubtitleType, track.index)
        await this.webosInternal.setMediaSubtitleOn(true)
      } catch (err) {
        // already logged
      }
    }
  }

  private setSubtitleCurrentIndex(track: ChannelTrackData): void {
    if (this.allowSubtitle) {
      this.webosInternal.setSubtitleCurrentIndex(track.index)
    }
  }

  async setSubtitleState(enable: boolean): Promise<boolean> {
    this.allowSubtitle = enable
    if (!this.allowSubtitle) {
      if (this.isMediaVideoLayer) {
        try {
          await this.webosInternal.setMediaSubtitle('none', 0)
          await this.webosInternal.setMediaSubtitleOn(false)
        } catch (err) {
          // already logged
        }
      } else if (this.subtitleOffIndex != undefined) {
        this.webosInternal.setSubtitleCurrentIndex(this.subtitleOffIndex)
      }
    }
    return this.allowSubtitle
  }

  getLocalMedias(): Promise<LocalMediaFile[]> {
    return this.webosInternal.getLocalMedias()
  }

  downloadMedia(media: Media): Promise<LocalMediaFile> {
    return this.webosInternal.downloadMedia(media)
  }

  removeMedia(mediaFile: LocalMediaFile): Promise<void> {
    return this.webosInternal.removeMedia(mediaFile)
  }

  setNativeVolumeDisplay(enabled: boolean): void {
    this.webosInternal.setTvVolumeUI(enabled ? '1' : '0')
  }

  getVolume(): Promise<number> {
    return this.webosInternal.getVolume()
  }

  setVolume(volume: number): Promise<number> {
    try {
      return this.webosInternal.setVolume(volume)
    } catch (err) {
      return Promise.resolve(0)
    }
  }

  async setVolumeDown(): Promise<number> {
    const volume = await this.getVolume()
    return this.setVolume(volume > 0 ? volume - 1 : 0)
  }

  async setVolumeUp(): Promise<number> {
    const volume = await this.getVolume()
    return this.setVolume(volume + 1)
  }

  getVolumeMute(): Promise<boolean> {
    return this.webosInternal.getVolumeMute()
  }

  setVolumeMute(muted: boolean): Promise<boolean> {
    return this.webosInternal.setVolumeMute(muted)
  }

  getExternalInputs(): Promise<ExternalInputData[]> {
    return this.webosInternal.getExternalInputs()
  }

  registerExternalInputListener(subject: BehaviorSubject<ExternalInputData[]>): void {
    this.webosInternal.registerExternalInputListener(subject)
  }

  async showHDMI(externalInput: ExternalInputData): Promise<void> {
    const fullscreenPosition: ChannelPosition = {
      left: 0,
      top: 0,
      width: window.innerWidth,
      height: window.innerHeight,
    }
    await this.webosInternal.setVideoSize(fullscreenPosition)
    await this.webosInternal.setExternalInput('HDMI', externalInput.index)
  }

  hideHDMI(): Promise<void> {
    return this.webosInternal.setExternalInput('TV', 0)
  }

  // Google Cast supported from webOS 23 (8.x.x) (model year UM or higher)
  // Update 23/03/26: Seems like it got backported to earlier models (ex: UR762)
  // https://procentric.developer.lge.com/docs/hcap-idcap-app-development/google-cast
  hasIntegratedGoogleCast(): boolean {
    return true
  }

  async initGoogleCastApp(): Promise<void> {
    try {
      this.logService.debug(`[WebOS] Registering Google Cast token...`)
      const tokenList = [{ id: 'com.webos.chromecast', token: this.GOOGLECAST_TOKEN }]
      await this.webosInternal.registerAppToken(tokenList)
    } catch (err: any) {
      this.logService.error(`[WebOS] Could not register Google Cast token`)
      this.gcInitializedSubject?.next(false)
      return
    }

    try {
      this.logService.debug(`[WebOS] Installing Google Cast related applications...`)
      const appIds = [
        'com.webos.chromecast',
        'com.webos.app.commercial.chromecastguide',
        'com.webos.chromecast-settings',
      ]
      await this.webosInternal.installApps(appIds)
    } catch (err: any) {
      this.logService.error(`[WebOS] Could not install Google Cast applications`)
      this.gcInitializedSubject?.next(false)
      return
    }

    // Enable the DIAL protocol (YouTube)
    const isDialEnabled = await this.webosInternal.getStringProperty('dial')
    if (isDialEnabled == '0') {
      // Should reboot for this to take effect
      await this.webosInternal.setStringProperty('dial', '1')
    }

    this.gcInitializedSubject?.next(true)
  }

  registerGoogleCastInitializedListener(subject: BehaviorSubject<boolean | undefined>): void {
    this.gcInitializedSubject = subject
  }

  // Called after initGoogleCastApp for Proxy specific initializations
  async initGoogleCastProxy(): Promise<void> {
    // Register Google Cast service activity listener
    document.addEventListener('idcap::chromecast_status_changed', this.onProxyChromecastStatusChanged, false)

    // Allow mDNS port
    const isMDNSBlocked = await this.webosInternal.getStringProperty('block_mdns_port')
    if (isMDNSBlocked == '1') {
      await this.webosInternal.setStringProperty('block_mdns_port', '0')
    }

    // Activate Chromecast service
    await this.enableGoogleCast()
  }

  // Called after initGoogleCastApp for SoftAP specific initializations
  async initGoogleCastSoftAP(): Promise<void> {
    // Register Google Cast service activity listener
    document.addEventListener('idcap::chromecast_status_changed', this.onSoftAPChromecastStatusChanged, false)

    // Activate Chromecast service
    await this.enableGoogleCast()

    // Disable SoftAP until user goes to GoogleCast app/page
    this.disableSoftAP()
  }

  // Using function like this to avoid duplicate event listeners with addEventListener using the reference
  onProxyChromecastStatusChanged = (param: any): void => {
    if (param.status === 'Error') {
      this.logService.info(`[WebOS] chromecast_status_changed: ${param?.status}: ${param?.errorText}`)
    } else {
      this.logService.info(`[WebOS] chromecast_status_changed: ${param?.status}`)
    }

    this.gcStatus = param.status
  }

  // Using function like this to avoid duplicate event listeners with addEventListener using the reference
  onSoftAPChromecastStatusChanged = (param: any): void => {
    if (param.status === 'Error') {
      this.logService.info(`[WebOS] chromecast_status_changed: ${param?.status}: ${param?.errorText}`)
    } else {
      this.logService.info(`[WebOS] chromecast_status_changed: ${param?.status}`)
    }

    if (param.status !== 'Casting' && this.gcStatus === 'Casting') {
      this.logService.info(`[WebOS] No longer casting, stopping Google Cast SoftAP`)
      this.disableSoftAP()
      this.removeGoogleCastSoftAPClosedListener()
    }

    this.gcStatus = param.status
  }

  async enableGoogleCast(): Promise<void> {
    const isChromecastActivated = await this.webosInternal.getStringProperty('chromecast')
    if (isChromecastActivated == '0') {
      await this.webosInternal.setStringProperty('chromecast', '1')
    }
  }

  async disableGoogleCast(): Promise<void> {
    const isChromecastActivated = await this.webosInternal.getStringProperty('chromecast')
    if (isChromecastActivated == '1') {
      await this.webosInternal.setStringProperty('chromecast', '0')
    }
  }

  async disableSoftAP(): Promise<void> {
    this.logService.info(`[WebOS] Disabling SoftAP`)
    try {
      const softap = await this.webosInternal.getSoftAPConfiguration()
      if (softap.enabled) {
        this.webosInternal.disableSoftAP()
      }
    } catch (err: any) {
      this.logService.error(`[WebOS] Could not disable SoftAP, error = ${err?.message}`)
    }
  }

  acceptGoogleCastTermsOfService(): Promise<void> {
    return Promise.resolve()
  }

  async enableGoogleCastUsageReporting(): Promise<void> {
    // Allow to send the device usage and crash report to Google
    const isAllowed = await this.webosInternal.getStringProperty('chromecast_data_registration')
    if (isAllowed == '0') {
      await this.webosInternal.setStringProperty('chromecast_data_registration', '1')
    }
  }

  // Do nothing since it is the same procedure for all user data (set security_level to 1 and reboot)
  // https://procentric.developer.lge.com/docs/hcap-idcap-app-development/google-cast#clear-credentials-and-casting-data
  clearGoogleCastData(): Promise<void> {
    return Promise.resolve()
  }

  // https://procentric.developer.lge.com/docs/hcap-idcap-app-development/google-cast#launch-the-guest-facing-google-cast-guide-application
  async openGoogleCastLocalPage(
    roomNumber: string,
    supportMessage: string,
    gcStateSubject: BehaviorSubject<GoogleCastState | undefined>
  ): Promise<void> {
    const launchChromecastGuide = async (gcState?: GoogleCastState): Promise<void> => {
      const pairingStatus = gcState?.pairingStatus ?? false
      const code = gcState?.code ?? ''
      const qrcodeUrl = gcState?.qrcodeUrl ?? ''
      const registerUrl = gcState?.registerUrl ?? ''
      const wifiSSID = gcState?.wifiSSID ?? ''
      const params: object = {
        soft_ap: false,
        room_number: roomNumber,
        tv_name: `[TV] ${roomNumber}`,
        support_message: supportMessage,
        ssid: wifiSSID,
        url: {
          QR: qrcodeUrl,
          display: registerUrl,
        },
        url_pin: code,
        start_page: pairingStatus ? 2 : 4,
        color_schemes: 'default',
        hide_2nd_screen: true,
      }
      return this.webosInternal.startApp('com.webos.app.commercial.chromecastguide', params, false)
    }

    try {
      await launchChromecastGuide(gcStateSubject.value)
    } catch (err: any) {
      this.logService.error('Could not launch chromecast-guide app')
    }

    if (this.gcStateSubscription) {
      this.gcStateSubscription?.unsubscribe()
    }
    this.gcStateSubject = gcStateSubject
    this.gcStateSubscription = this.gcStateSubject.subscribe(async (gcState?: GoogleCastState) => {
      const currentApp = await this.webosInternal.getCurrentApp()
      if (currentApp.appId === 'com.webos.app.commercial.chromecastguide') {
        launchChromecastGuide(gcState)
      }
    })

    return Promise.resolve()
  }

  private readonly onForegroundAppChanged = (param: any): void => {
    const appId = param?.appId ?? ''
    this.logService.info(`[WebOS] (idcap::foreground_app_changed) ${appId}`)
    switch (appId) {
      case 'com.webos.chromecast':
        break
      case 'com.webos.app.commercial.chromecastguide':
        break
      default:
        this.logService.info(`[WebOS] Focus has changed, stopping Google Cast SoftAP`)
        this.disableSoftAP()
        this.removeGoogleCastSoftAPClosedListener()
        break
    }
  }

  private addGoogleCastSoftAPClosedListener(): void {
    document.addEventListener('idcap::foreground_app_changed', this.onForegroundAppChanged, false)
  }

  private removeGoogleCastSoftAPClosedListener(): void {
    document.removeEventListener('idcap::foreground_app_changed', this.onForegroundAppChanged, false)
  }

  // Opening com.webos.app.commercial.chromecastguide will also start the soft AP
  // https://procentric.developer.lge.com/docs/hcap-idcap-app-development/google-cast#launch-the-guest-facing-google-cast-guide-application
  async openGoogleCastSoftAPPage(roomNumber: string, pairingStatus: boolean, supportMessage: string): Promise<void> {
    // Activate Google Cast service
    await this.enableGoogleCast()

    // Register listener that will close SoftAP if we leave the cast environment
    this.addGoogleCastSoftAPClosedListener()

    const params: object = {
      soft_ap: true,
      room_number: roomNumber,
      tv_name: `[TV] ${roomNumber}`,
      support_message: supportMessage,
      start_page: pairingStatus ? 2 : 0,
      channel_number: 0,
      signal_strength: 0,
      is_ssid_hidden: false,
    }
    this.webosInternal.startApp('com.webos.app.commercial.chromecastguide', params, false)
  }

  pollGoogleCastSoftAP(): Promise<any> {
    return Promise.resolve()
  }

  launchGoogleCastSettings(): Promise<void> {
    this.webosInternal.startApp('com.webos.chromecast-settings', {}, false)
    return Promise.resolve()
  }

  async initNetflixApp(): Promise<void> {
    try {
      this.logService.info(`[WebOS] Registering Netflix token`)
      const tokenList = [{ id: 'netflix', token: this.NETFLIX_TOKEN }]
      await this.webosInternal.registerAppToken(tokenList)
    } catch (err: any) {
      this.logService.error(`[WebOS] Could not register Netflix token`)
      return
    }

    try {
      this.logService.info(`[WebOS] Installing Netflix`)
      const appIds = ['netflix']
      await this.webosInternal.installApps(appIds)
    } catch (err: any) {
      this.logService.error(`[WebOS] Could not install Netflix`)
      return
    }
  }

  adminOpenAppsSettings(): void {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  downloadAndUpgradeApp(deviceId: string): void {
    // do nothing
  }

  canDownloadAndUpgradeApp(): boolean {
    return false
  }

  hasCustomVOD(): boolean {
    return false
  }

  getShakaPlatformConfig(): any {
    return undefined
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodStart(vod: VODVideo, pos: ChannelPosition): void {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodPlay(vod: VODVideo): void {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodPause(vod: VODVideo): void {
    // do nothing
  }

  vodStop(): void {
    // do nothing
  }

  vodGetCurrentTime(): number {
    return 0
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodSpeed(vod: VODVideo, speed: number): void {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodSeekTo(vod: VODVideo, milliseconds: number): Promise<void> {
    return Promise.resolve()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodJumpBackward(vod: VODVideo, milliseconds: number): Promise<void> {
    return Promise.resolve()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  vodJumpForward(vod: VODVideo, milliseconds: number): Promise<void> {
    return Promise.resolve()
  }

  initVirtualKeyboard(): void {
    return
  }
}
